import React, { useState, useEffect, useRef } from "react";
import { trackData } from "../../../../Utils/attribution/CheckoutAttribution";
import "./PFHBanner.css";

interface PFHBannerProps {
  remoteConfig: {
    enabled: boolean;
    image_url: string;
    cta_url: string;
    video_url?: string;
    cta_text?: string;
  } | null;
  onImageClick: (url: string) => void;
}

const PFHBanner: React.FC<PFHBannerProps> = ({
  remoteConfig,
  onImageClick,
}) => {
  // Carousel images and URLs
  const carouselImages = [
    "https://hfjekfswossjlpcndyye.supabase.co/storage/v1/object/public/images/Order1-2.webp",
    "https://hfjekfswossjlpcndyye.supabase.co/storage/v1/object/public/images/Order3-4.webp",
  ];

  const carouselUrls = [
    "https://nationalfinanceolympiad.com/1-2-guidebook",
    "https://nationalfinanceolympiad.com/3-4-guidebook-2",
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const autoplayIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Commented out video-related code
  // const [isVideoMuted, setIsVideoMuted] = React.useState(true);
  // const videoRef = React.useRef<HTMLVideoElement>(null);

  // Commented out video check
  // const hasVideo = (remoteConfig as any).video_url;
  // const ctaText = (remoteConfig as any).cta_text || "Explore";

  // Autoplay functionality
  useEffect(() => {
    if (!remoteConfig?.enabled) {
      return;
    }

    if (!isHovered) {
      autoplayIntervalRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % carouselImages.length);
      }, 5000);
    } else {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
      }
    }

    return () => {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current);
      }
    };
  }, [isHovered, carouselImages.length, remoteConfig?.enabled]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    if (autoplayIntervalRef.current) {
      clearInterval(autoplayIntervalRef.current);
    }
    // Resume autoplay after 3 seconds
    setTimeout(() => {
      if (!isHovered) {
        autoplayIntervalRef.current = setInterval(() => {
          setCurrentIndex(
            (prevIndex) => (prevIndex + 1) % carouselImages.length
          );
        }, 5000);
      }
    }, 3000);
  };

  const goToPrevious = () => {
    goToSlide(
      (currentIndex - 1 + carouselImages.length) % carouselImages.length
    );
  };

  const goToNext = () => {
    goToSlide((currentIndex + 1) % carouselImages.length);
  };

  const handleImageClick = () => {
    const clickedUrl = carouselUrls[currentIndex];
    trackData("user_clicked_banner", null, null, {
      banner_type: "pfh_banner_carousel",
      image_index: currentIndex,
      click_url: clickedUrl,
    });
    onImageClick(clickedUrl);
  };

  if (!remoteConfig?.enabled) {
    return null;
  }

  return (
    <div
      className="pfh-banner-container"
      style={{ position: "relative" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="carousel-wrapper">
        <button
          className="carousel-arrow carousel-arrow-left"
          onClick={(e) => {
            e.stopPropagation();
            goToPrevious();
          }}
          aria-label="Previous image"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>

        <button
          className="carousel-arrow carousel-arrow-right"
          onClick={(e) => {
            e.stopPropagation();
            goToNext();
          }}
          aria-label="Next image"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>

        <div className="carousel-slides">
          {carouselImages.map((image, index) => (
            <div
              key={index}
              className={`carousel-slide ${
                index === currentIndex ? "active" : ""
              }`}
            >
              <img
                src={image}
                alt={`Banner ${index + 1}`}
                className="pfh-banner-image"
                onClick={handleImageClick}
              />
            </div>
          ))}
        </div>

        <div className="carousel-dots">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${
                index === currentIndex ? "active" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                goToSlide(index);
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Commented out video code */}
      {/* {hasVideo ? (
        <button
          onClick={onImageClick}
          style={{
            width: "100%",
            padding: 0,
            border: "none",
            background: "none",
            cursor: "pointer",
            position: "relative",
            display: "block",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <video
            ref={videoRef}
            src={(remoteConfig as any).video_url}
            className="pfh-banner-image"
            style={{ width: "100%", height: "auto", display: "block" }}
            autoPlay
            muted={isVideoMuted}
            loop
            playsInline
          ></video>
          <span
            style={{
              display: "block",
              width: "100%",
              background: "rgba(36, 108, 193, 1)",
              color: "#fff",
              fontWeight: 600,
              fontSize: 18,
              padding: "16px 0",
              textAlign: "center",

              borderBottomLeftRadius: 8,
              borderBottomRightRadius: 8,
            }}
          >
            {ctaText}
          </span>
          <span
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 3,
              background: "rgba(0,0,0,0.5)",
              borderRadius: "50%",
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsVideoMuted((m) => !m);
            }}
          >
            {isVideoMuted ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                <path d="M4 9v6h4l5 5V4L8 9H4zm13.5 3c0-1.77-.77-3.29-2-4.29v8.58c1.23-1 2-2.52 2-4.29z" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                <path d="M16.5 12c0-1.77-.77-3.29-2-4.29v8.58c1.23-1 2-2.52 2-4.29zm3.5 0c0 2.53-1.17 4.77-3 6.18v-2.06c1.19-1.02 2-2.54 2-4.12s-.81-3.1-2-4.12V5.82c1.83 1.41 3 3.65 3 6.18zM4 9v6h4l5 5V4L8 9H4z" />
              </svg>
            )}
          </span>
        </button>
      ) : (
        <img
          src={remoteConfig.image_url}
          alt="PFH Banner"
          className="pfh-banner-image"
          style={{ width: "100%", height: "auto", display: "block" }}
        />
      )} */}
    </div>
  );
};

export default PFHBanner;
